<template>
  <div class="p-6 md:p-10 bg-white min-h-screen text-slate-800 font-sans antialiased select-none">
    
    <!-- Header -->
    <div class="max-w-7xl mx-auto mb-8 pb-6 border-b border-slate-100">
      <div class="flex items-center gap-2 mb-2">
        <span class="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black uppercase rounded-full tracking-wider border border-blue-100">
          Module Rapports
        </span>
      </div>
      <h1 class="text-3xl font-black text-slate-900 tracking-tight">Export de Données Ticketing</h1>
      <p class="text-sm text-slate-500 font-medium mt-1">Configurez vos critères de filtrage pour générer et télécharger vos rapports d'activité au format CSV ou Excel (XLSX).</p>
    </div>

    <div class="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8 items-start">

      <!-- Panneau de configuration (Gauche - 2 colonnes) -->
      <div class="lg:col-span-2 space-y-6">

        <!-- 1. Période d'analyse -->
        <div class="bg-slate-50/80 rounded-2xl p-6 border border-slate-200/80 shadow-sm">
          <div class="flex items-center gap-3 mb-6">
            <div class="w-10 h-10 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center text-blue-600 shadow-sm">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 class="text-base font-black text-slate-900">Période d'analyse</h2>
              <p class="text-xs text-slate-500 font-medium">Sélectionnez la plage temporelle du rapport</p>
            </div>
          </div>

          <div class="grid sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Date de début</label>
              <input
                v-model="startDate"
                type="date"
                class="w-full bg-white border border-slate-200 rounded-xl py-3 px-4 font-semibold text-xs text-slate-800 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all shadow-sm"
              >
            </div>
            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Date de fin</label>
              <input
                v-model="endDate"
                type="date"
                class="w-full bg-white border border-slate-200 rounded-xl py-3 px-4 font-semibold text-xs text-slate-800 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all shadow-sm"
              >
            </div>
          </div>

          <!-- Raccourcis de période -->
          <div class="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200/60">
            <button
              @click="setPeriod('today')"
              class="px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 rounded-xl transition-all cursor-pointer shadow-sm"
            >
              Aujourd'hui
            </button>
            <button
              @click="setPeriod('week')"
              class="px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 rounded-xl transition-all cursor-pointer shadow-sm"
            >
              7 derniers jours
            </button>
            <button
              @click="setPeriod('month')"
              class="px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 rounded-xl transition-all cursor-pointer shadow-sm"
            >
              Ce mois-ci
            </button>
          </div>
        </div>

        <!-- 2. Filtres optionnels -->
        <div class="bg-slate-50/80 rounded-2xl p-6 border border-slate-200/80 shadow-sm">
          <div class="flex items-center gap-3 mb-6">
            <div class="w-10 h-10 bg-purple-50 border border-purple-100 rounded-xl flex items-center justify-center text-purple-600 shadow-sm">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <div>
              <h2 class="text-base font-black text-slate-900">Filtres optionnels</h2>
              <p class="text-xs text-slate-500 font-medium">Restreignez le périmètre des données exportées</p>
            </div>
          </div>

          <div class="grid sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Statut</label>
              <select
                v-model="selectedStatus"
                class="w-full bg-white border border-slate-200 rounded-xl py-3 px-4 font-semibold text-xs text-slate-800 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all cursor-pointer shadow-sm"
              >
                <option value="">Tous les statuts</option>
                <option value="WAITING">En attente</option>
                <option value="CALLED">Appelé</option>
                <option value="SKIPPED">Ignoré</option>
                <option value="DONE">Terminé</option>
                <option value="ARCHIVED">Archivé</option>
              </select>
            </div>
            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">File d'attente</label>
              <select
                v-model="selectedQueue"
                class="w-full bg-white border border-slate-200 rounded-xl py-3 px-4 font-semibold text-xs text-slate-800 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 transition-all cursor-pointer shadow-sm"
              >
                <option value="">Toutes les files</option>
                <option v-for="queue in queues" :key="queue._id" :value="queue._id">
                  {{ queue.name }} ({{ queue.prefix }})
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- 3. Sélection des champs -->
        <div class="bg-slate-50/80 rounded-2xl p-6 border border-slate-200/80 shadow-sm">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 shadow-sm">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 class="text-base font-black text-slate-900">Colonnes à exporter</h2>
                <p class="text-xs text-slate-500 font-medium">Cochez les données à inclure dans le fichier</p>
              </div>
            </div>

            <div class="flex gap-2">
              <button
                @click="selectAllFields"
                class="px-3.5 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/60 rounded-xl transition-all cursor-pointer shadow-sm"
              >
                Tout cocher
              </button>
              <button
                @click="deselectAllFields"
                class="px-3.5 py-2 text-xs font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200/60 rounded-xl transition-all cursor-pointer shadow-sm"
              >
                Tout décocher
              </button>
            </div>
          </div>

          <div class="grid sm:grid-cols-2 gap-3">
            <div
              v-for="field in availableFields"
              :key="field.key"
              @click="toggleField(field.key)"
              class="flex items-start gap-3 p-3.5 rounded-xl border transition-all cursor-pointer group shadow-sm"
              :class="selectedFields.includes(field.key)
                ? 'bg-white border-blue-500 ring-1 ring-blue-500/20'
                : 'bg-white border-slate-200/80 hover:border-slate-300'"
            >
              <div class="flex-shrink-0 mt-0.5">
                <div
                  class="w-4 h-4 rounded-md border flex items-center justify-center transition-all"
                  :class="selectedFields.includes(field.key)
                    ? 'bg-blue-600 border-blue-600'
                    : 'border-slate-300 group-hover:border-slate-400 bg-slate-50'"
                >
                  <svg
                    v-if="selectedFields.includes(field.key)"
                    class="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-black text-slate-800">{{ field.label }}</p>
                <p class="text-[11px] text-slate-500 truncate mt-0.5 font-medium">{{ field.description }}</p>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Panneau de droite - Synthèse & Action -->
      <div class="space-y-6 sticky top-6">

        <!-- Card Aperçu -->
        <div class="bg-[#0B0F19] rounded-2xl p-6 text-white shadow-xl border border-slate-800 relative overflow-hidden">
          <div class="relative z-10">
            <div class="flex items-center justify-between mb-6 pb-4 border-b border-slate-800">
              <h3 class="text-xs font-black uppercase tracking-widest text-slate-400">Synthèse de l'export</h3>
              <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
            </div>

            <div v-if="preview" class="space-y-5">
              <div>
                <p class="text-[10px] text-slate-400 uppercase tracking-widest mb-1 font-black">Volume estimé</p>
                <div class="flex items-baseline gap-2">
                  <p class="text-4xl font-black text-white tracking-tight">{{ preview.totalTickets }}</p>
                  <span class="text-xs text-slate-400 font-bold">tickets</span>
                </div>
              </div>

              <div class="pt-4 border-t border-slate-800">
                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Répartition par statut</p>
                <div class="space-y-2">
                  <div v-for="(count, status) in preview.statusBreakdown" :key="status" class="flex justify-between items-center text-xs">
                    <span class="text-slate-300 font-semibold">{{ getStatusLabel(status) }}</span>
                    <span class="font-bold text-white bg-slate-800 px-2.5 py-1 rounded-lg border border-slate-700/50">{{ count }}</span>
                  </div>
                </div>
              </div>

              <div class="pt-4 border-t border-slate-800 flex justify-between items-center text-xs">
                <span class="text-slate-400 font-semibold">Colonnes sélectionnées</span>
                <span class="font-black text-blue-400">{{ selectedFields.length }} / {{ availableFields.length }}</span>
              </div>
            </div>

            <div v-else class="text-center py-8">
              <svg class="w-10 h-10 mx-auto text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p class="text-xs text-slate-400 font-medium">Sélectionnez une période valide</p>
            </div>

            <button
              @click="loadPreview"
              :disabled="!startDate || !endDate"
              class="w-full mt-6 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 py-3.5 px-4 rounded-xl font-bold text-xs uppercase tracking-wider transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-lg"
            >
              Actualiser l'aperçu
            </button>
          </div>
        </div>

        <!-- Action Téléchargement -->
        <div class="bg-slate-50/80 rounded-2xl p-6 border border-slate-200/80 shadow-sm">
          <div class="space-y-3">
            <!-- Bouton CSV -->
            <button
              @click="exportCSV"
              :disabled="!canExport || isExporting"
              class="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider py-4 px-6 rounded-xl shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed flex items-center justify-center gap-2.5 cursor-pointer"
            >
              <svg
                v-if="isExporting && exportFormat === 'csv'"
                class="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>{{ isExporting && exportFormat === 'csv' ? 'Génération du CSV...' : 'Télécharger au format CSV' }}</span>
            </button>

            <!-- Bouton XLSX -->
            <button
              @click="exportXLSX"
              :disabled="!canExport || isExporting"
              class="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider py-4 px-6 rounded-xl shadow-lg shadow-blue-500/20 transition-all disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed flex items-center justify-center gap-2.5 cursor-pointer"
            >
              <svg
                v-if="isExporting && exportFormat === 'xlsx'"
                class="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>{{ isExporting && exportFormat === 'xlsx' ? 'Génération Excel...' : 'Télécharger Excel (XLSX)' }}</span>
            </button>
          </div>

          <!-- Barre de progression -->
          <div v-if="isExporting" class="mt-4">
            <div class="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
              <div
                class="h-full transition-all duration-300 ease-out rounded-full bg-blue-600"
                :style="{ width: downloadProgress + '%' }"
              ></div>
            </div>
            <p class="text-[11px] text-center mt-2 font-bold text-slate-500">
              Traitement en cours ({{ downloadProgress }}%)
            </p>
          </div>

          <p v-else class="text-xs text-center mt-4">
            <span v-if="!canExport" class="text-rose-600 font-bold">Sélectionnez une période et au moins une colonne.</span>
            <span v-else class="text-slate-400 font-medium">Prêt pour l'exportation des données</span>
          </p>
        </div>

      </div>

    </div>

    <!-- Modal / Overlay de chargement -->
    <div
      v-if="isExporting"
      class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-2xl p-8 shadow-2xl max-w-sm w-full text-center border border-slate-200">
        <div class="w-14 h-14 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-600">
          <svg class="w-7 h-7 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </div>

        <h3 class="text-base font-black text-slate-900 mb-1 uppercase tracking-tight">Exportation en cours</h3>
        <p class="text-xs text-slate-500 mb-6 font-medium">
          Compilation de votre fichier {{ exportFormat === 'csv' ? 'CSV' : 'Excel (XLSX)' }}...
        </p>

        <div class="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden mb-2">
          <div
            class="h-full transition-all duration-300 rounded-full bg-blue-600"
            :style="{ width: downloadProgress + '%' }"
          ></div>
        </div>
        <p class="text-xs font-black text-blue-600 text-right mb-6">{{ downloadProgress }}%</p>

        <div class="bg-slate-50 p-3 rounded-xl border border-slate-200 text-left">
          <p class="text-[10px] text-slate-500 font-medium leading-relaxed">
            <strong>Note :</strong> Veuillez patienter pendant la préparation de vos données.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';

const api = axios.create({
  baseURL: 'https://api.datafuse.fr/api/agency',
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token') || localStorage.getItem('userToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const startDate = ref('');
const endDate = ref('');
const selectedStatus = ref('');
const selectedQueue = ref('');
const availableFields = ref([]);
const selectedFields = ref([]);
const queues = ref([]);
const preview = ref(null);
const isExporting = ref(false);
const downloadProgress = ref(0);
const exportFormat = ref('');

const canExport = computed(() => {
  return startDate.value && endDate.value && selectedFields.value.length > 0;
});

const setDefaultDates = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  endDate.value = today.toISOString().split('T')[0];
  startDate.value = firstDay.toISOString().split('T')[0];
};

const setPeriod = (type) => {
  const today = new Date();
  endDate.value = today.toISOString().split('T')[0];

  if (type === 'today') {
    startDate.value = today.toISOString().split('T')[0];
  } else if (type === 'week') {
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    startDate.value = weekAgo.toISOString().split('T')[0];
  } else if (type === 'month') {
    const monthAgo = new Date(today);
    monthAgo.setMonth(today.getMonth() - 1);
    startDate.value = monthAgo.toISOString().split('T')[0];
  }

  loadPreview();
};

const loadFields = async () => {
  try {
    const response = await api.get('/ticketing-fields');
    if (response.data.success) {
      availableFields.value = response.data.fields;
      selectedFields.value = availableFields.value.map(f => f.key);
    }
  } catch (error) {
    console.error('Erreur chargement champs:', error);
  }
};

const loadQueues = async () => {
  try {
    const response = await api.get('/ticketing-queues');
    if (response.data.success) {
      queues.value = response.data.queues;
    }
  } catch (error) {
    console.error('Erreur chargement files:', error);
  }
};

const loadPreview = async () => {
  if (!startDate.value || !endDate.value) return;

  try {
    const params = {
      startDate: startDate.value,
      endDate: endDate.value
    };

    if (selectedStatus.value) params.status = selectedStatus.value;
    if (selectedQueue.value) params.queueId = selectedQueue.value;

    const response = await api.get('/ticketing-preview', { params });
    preview.value = response.data;
  } catch (error) {
    console.error('Erreur chargement aperçu:', error);
  }
};

const exportCSV = async () => {
  if (!canExport.value || isExporting.value) return;

  isExporting.value = true;
  exportFormat.value = 'csv';
  downloadProgress.value = 0;

  try {
    const params = {
      startDate: startDate.value,
      endDate: endDate.value,
      fields: selectedFields.value.join(',')
    };

    if (selectedStatus.value) params.status = selectedStatus.value;
    if (selectedQueue.value) params.queueId = selectedQueue.value;

    const response = await api.get('/ticketing-export-csv', {
      params,
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.lengthComputable) {
          downloadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        } else {
          downloadProgress.value = Math.min(downloadProgress.value + 15, 90);
        }
      }
    });

    downloadProgress.value = 100;

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'rapport-ticketing.csv';
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1];
      }
    }

    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    console.error('Erreur export CSV:', error);
  } finally {
    setTimeout(() => {
      isExporting.value = false;
      exportFormat.value = '';
      downloadProgress.value = 0;
    }, 800);
  }
};

const exportXLSX = async () => {
  if (!canExport.value || isExporting.value) return;

  isExporting.value = true;
  exportFormat.value = 'xlsx';
  downloadProgress.value = 0;

  try {
    const params = {
      startDate: startDate.value,
      endDate: endDate.value,
      fields: selectedFields.value.join(',')
    };

    if (selectedStatus.value) params.status = selectedStatus.value;
    if (selectedQueue.value) params.queueId = selectedQueue.value;

    const response = await api.get('/ticketing-export-xlsx', {
      params,
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.lengthComputable) {
          downloadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        } else {
          downloadProgress.value = Math.min(downloadProgress.value + 15, 90);
        }
      }
    });

    downloadProgress.value = 100;

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'rapport-ticketing.xlsx';
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1];
      }
    }

    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    console.error('Erreur export XLSX:', error);
  } finally {
    setTimeout(() => {
      isExporting.value = false;
      exportFormat.value = '';
      downloadProgress.value = 0;
    }, 800);
  }
};

const toggleField = (fieldKey) => {
  const index = selectedFields.value.indexOf(fieldKey);
  if (index > -1) {
    selectedFields.value.splice(index, 1);
  } else {
    selectedFields.value.push(fieldKey);
  }
};

const selectAllFields = () => {
  selectedFields.value = availableFields.value.map(f => f.key);
};

const deselectAllFields = () => {
  selectedFields.value = [];
};

const getStatusLabel = (status) => {
  const statusMap = {
    'WAITING': 'En attente',
    'CALLED': 'Appelé',
    'SKIPPED': 'Ignoré',
    'DONE': 'Terminé',
    'ARCHIVED': 'Archivé'
  };
  return statusMap[status] || status;
};

onMounted(async () => {
  await loadFields();
  await loadQueues();
  setDefaultDates();
  loadPreview();
});
</script>